import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Breadcrumbs, Link as MuiLink, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ProductForm from '../../components/Products/ProductForm';
import { getData, putData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { MdEdit, MdArrowBack } from "react-icons/md";

const initialFormState = {
    title: "",
    description: "",
    key_features: "",
    how_to_use: "",
    category_id: "",
    subcategory_id: "",
    upc: "",
    product_images: [],
    productVideos: [],
    unitType: "",
    unitValue: 0,
    unitName: "",
    status: "approved",
    isPublished: false,
    brand: "",
    ideal_for: "",
    applied_for: "",
    container_type: "",
    country_of_orgin: "",
    color: "",
    gst: "",
    hsn_code: "",
    codAvailable: false,
    tags: [],
    b2c_mrp: '',
    b2c_discountType: '',
    b2c_discountValue: '',
    b2c_sellingPrice: '',
    b2b_discountType: '',
    b2b_discountValue: '',
    b2b_sellingPrice: '',
    visibility: ['b2b', 'b2c'],
    isFree: false,
    thresholdAmount: '',
    autoAdd: false,
    minimumStockQuantity: '',
    length: '',
    width: '',
    height: '',
    dimensionUnit: '',
    weight: '',
    weightUnit: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    badge: '',
    is_featured: false,
    ingredients: '',
    skin_type: '',
    hair_type: '',
    fragrance_notes: '',
    is_vegan: false,
    cruelty_free: false,
    organic: false,
    shipping_class: '',
    return_policy: 0,
    manufacturerName: '',
    manufacturerAddress: '',
    manufacturingLicenseNumber: ''
};

const EditProduct = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(initialFormState);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const fetchCategories = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('categories/list?limit=1000', null, token);
            setCategories(Array.isArray(response.categories) ? response.categories : []);
        } catch (error) {
            console.error('Fetch categories failed:', error);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        if (!categoryId) {
            setSubcategories([]);
            return;
        }
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`subcategories/list?limit=1000&category_id=${categoryId}`, null, token);
            setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
        } catch (error) {
            setSubcategories([]);
            console.error('Fetch subcategories failed:', error);
        }
    };

    const fetchProductDetails = async () => {
        setLoading(true);
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const product = await getData(PRODUCT_ROUTES.GET_PRODUCT_DETAILS + productId, null, token);

            const productData = product.product || product.data || product;

            if (productData && productData._id) {
                let parsedDimensions = productData.dimensions;
                if (typeof parsedDimensions === 'string') {
                    try { parsedDimensions = JSON.parse(parsedDimensions); } catch (e) { parsedDimensions = {}; }
                }

                const newForm = {
                    ...initialFormState,
                    ...productData,
                    category_id: productData.category_id?._id || productData.category_id || '',
                    subcategory_id: productData.subcategory_id?._id || productData.subcategory_id || '',
                    product_images: [
                        ...(Array.isArray(productData.product_images) ? productData.product_images : []),
                        ...(Array.isArray(productData.productVideos) ? productData.productVideos : [])
                    ],
                    tags: (() => {
                        const rawTags = productData.tags;
                        if (Array.isArray(rawTags)) return rawTags;
                        if (typeof rawTags === 'string') return rawTags.split(',').map(t => t.trim()).filter(t => t.length > 0);
                        return [];
                    })(),
                    visibility: (() => {
                        const rawVis = productData.visibility;
                        if (Array.isArray(rawVis)) return rawVis;
                        if (typeof rawVis === 'string') return rawVis.split(',').map(v => v.trim()).filter(v => v.length > 0);
                        return ['b2b', 'b2c'];
                    })(),
                    length: parsedDimensions?.length ?? '',
                    width: parsedDimensions?.width ?? '',
                    height: parsedDimensions?.height ?? '',
                    dimensionUnit: parsedDimensions?.unit ?? '',
                    weight: parsedDimensions?.weight ?? '',
                    weightUnit: parsedDimensions?.weightUnit ?? ''
                };

                // Ensure no null values for controlled inputs to prevent React warnings and UI glitches
                Object.keys(newForm).forEach(key => {
                    if (newForm[key] === null) newForm[key] = '';
                });

                setForm(newForm);

                if (productData.category_id) {
                    fetchSubcategories(productData.category_id?._id || productData.category_id);
                }
            } else {
                console.error('Invalid product data received:', product);
                throw new Error('Could not load product details');
            }
        } catch (error) {
            console.error('Fetch product failed:', error);
            alert('Failed to fetch product details');
            navigate('/productmanagement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchProductDetails();
    }, [productId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedForm = { ...form, [name]: value };

        const calculatePrice = (mrp, discountType, discountValue) => {
            const m = parseFloat(mrp) || 0;
            const d = parseFloat(discountValue) || 0;
            if (discountType === 'flat') return Math.max(0, m - d).toFixed(0);
            if (discountType === 'percentage') return Math.max(0, m - (m * Math.min(100, d) / 100)).toFixed(0);
            return m.toFixed(0);
        };

        if (['b2c_mrp', 'b2c_discountType', 'b2c_discountValue'].includes(name)) {
            updatedForm.b2c_sellingPrice = calculatePrice(updatedForm.b2c_mrp, updatedForm.b2c_discountType, updatedForm.b2c_discountValue);
        }
        if (['b2c_mrp', 'b2b_discountType', 'b2b_discountValue'].includes(name)) {
            updatedForm.b2b_sellingPrice = calculatePrice(updatedForm.b2c_mrp, updatedForm.b2b_discountType, updatedForm.b2b_discountValue);
        }

        if (name === 'category_id') {
            updatedForm.subcategory_id = '';
            fetchSubcategories(e.target.value);
        }
        setForm(updatedForm);
    };

    const handleUpdateProduct = async () => {
        setSubmitting(true);
        try {
            const tokenValue = sessionStorage.getItem('token');
            const userId = sessionStorage.getItem('userId');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';

            const formData = new FormData();
            const dimensionFields = ['length', 'width', 'height', 'dimensionUnit', 'weight', 'weightUnit'];
            const numericFields = ['b2c_mrp', 'b2c_discountValue', 'b2c_sellingPrice', 'b2b_discountValue', 'b2b_sellingPrice', 'thresholdAmount', 'minimumStockQuantity', 'return_policy'];

            // We'll track if we added any images or videos
            let hasImages = false;
            let hasVideos = false;

            Object.entries(form).forEach(([key, value]) => {
                if (key === 'product_images' && Array.isArray(value)) {
                    if (value.length === 0) {
                        // Only append empty strings if we explicitly want to clear the media
                        formData.append('product_images', '');
                        formData.append('productVideos', '');
                    } else {
                        value.forEach(img => {
                            if (img instanceof File) {
                                if (img.type.startsWith('video/')) {
                                    formData.append('productVideos', img);
                                    hasVideos = true;
                                } else {
                                    formData.append('product_images', img);
                                    hasImages = true;
                                }
                            } else if (typeof img === 'string' && img.trim() !== '') {
                                // Keep the full URL as is. The backend handles full URLs correctly.
                                // Stripping the domain was causing broken paths and "removed" images.
                                const isVideo = /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(img);
                                if (isVideo) {
                                    formData.append('productVideos', img);
                                    hasVideos = true;
                                } else {
                                    formData.append('product_images', img);
                                    hasImages = true;
                                }
                            }
                        });
                    }
                } else if (key === 'productVideos' || key === 'productVideo') {
                    // Skip as we handled it via product_images merging
                } else if (dimensionFields.includes(key) || key === 'dimensions') {
                    // Handled separately below
                } else if (numericFields.includes(key)) {
                    // Only append if it's a valid number or non-empty
                    if (value !== '' && value !== null && value !== undefined) {
                        formData.append(key, Number(value));
                    }
                } else if (key === '_id' || key === 'createdAt' || key === 'updatedAt' || key === 'createdBy' || key === '__v') {
                    // skip internal read-only fields
                } else if (key === 'tags' || key === 'visibility') {
                    if (Array.isArray(value)) formData.append(key, value.join(','));
                } else if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });

            // Dimensions structure
            const dimensions = {
                length: form.length ? Number(form.length) : undefined,
                width: form.width ? Number(form.width) : undefined,
                height: form.height ? Number(form.height) : undefined,
                unit: form.dimensionUnit || undefined,
                weight: form.weight ? Number(form.weight) : undefined,
                weightUnit: form.weightUnit || undefined
            };
            Object.keys(dimensions).forEach(k => dimensions[k] === undefined && delete dimensions[k]);
            if (Object.keys(dimensions).length > 0) {
                formData.set('dimensions', JSON.stringify(dimensions));
            }

            // Do not send createdBy on update to avoid 400 if userId is missing or if backend is strict

            await putData(`product/update/${productId}`, formData, token);
            alert('Product updated successfully!');
            navigate('/productmanagement');
        } catch (error) {
            console.error('Update product failed:', error);
            alert('Failed to update product: ' + (error.message || 'Server error'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Mulish, sans-serif' }}>
                        Edit Product
                    </Typography>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                        <MuiLink underline="hover" color="inherit" onClick={() => navigate('/productmanagement')} sx={{ cursor: 'pointer' }}>
                            Product Management
                        </MuiLink>
                        <Typography color="text.primary">Edit Product</Typography>
                    </Breadcrumbs>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<MdArrowBack />}
                    onClick={() => navigate('/productmanagement')}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to List
                </Button>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #e5e7eb' }}>
                <ProductForm
                    form={form}
                    onFormChange={handleChange}
                    onCancel={() => navigate('/productmanagement')}
                    onSubmit={handleUpdateProduct}
                    categories={categories}
                    subcategories={subcategories}
                    isEdit={true}
                    formMode="edit"
                />
                {submitting && (
                    <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default EditProduct;
